import React, { useState, useEffect } from 'react';
import { X, Calendar, User, Building2, Sparkles, DollarSign } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

interface Contact {
  id: string;
  name: string;
}

interface Provider {
  id: string;
  name: string;
  business_name: string;
  category_id: string;
}

interface Service {
  id: string;
  name: string;
  price: number;
  category_id: string;
}

interface NewImageConsultantAppointmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const NewImageConsultantAppointmentModal: React.FC<NewImageConsultantAppointmentModalProps> = ({
  isOpen,
  onClose,
  onSuccess
}) => {
  const { user } = useAuth();
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [providers, setProviders] = useState<Provider[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [filteredServices, setFilteredServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchContact, setSearchContact] = useState('');
  const [formData, setFormData] = useState({
    contact_id: '',
    provider_id: '',
    service_id: '',
    appointment_date: '',
    status: 'scheduled' as 'scheduled' | 'completed' | 'cancelled' | 'no_show',
    price_paid: 0,
    notes: ''
  });

  useEffect(() => {
    if (isOpen) {
      loadData();
    }
  }, [isOpen]);

  useEffect(() => {
    if (formData.provider_id) {
      const provider = providers.find(p => p.id === formData.provider_id);
      if (provider) {
        const filtered = services.filter(s => s.category_id === provider.category_id);
        setFilteredServices(filtered);
        if (!filtered.find(s => s.id === formData.service_id)) {
          setFormData(prev => ({ ...prev, service_id: '' }));
        }
      }
    } else {
      setFilteredServices([]);
    }
  }, [formData.provider_id, providers, services]);

  useEffect(() => {
    if (formData.service_id) {
      const service = services.find(s => s.id === formData.service_id);
      if (service && formData.price_paid === 0) {
        setFormData(prev => ({ ...prev, price_paid: service.price }));
      }
    }
  }, [formData.service_id, services]);

  const loadData = async () => {
    try {
      const [contactsData, providersData, servicesData] = await Promise.all([
        supabase.from('contacts').select('id, name').order('name'),
        supabase.from('image_consultant_providers').select('id, name, business_name, category_id').eq('is_active', true).order('name'),
        supabase.from('image_consultant_services').select('id, name, price, category_id').order('name')
      ]);

      if (contactsData.data) setContacts(contactsData.data);
      if (providersData.data) setProviders(providersData.data);
      if (servicesData.data) setServices(servicesData.data);
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { error } = await supabase
        .from('image_consultant_appointments')
        .insert([{
          ...formData,
          service_id: formData.service_id || null,
          created_by: user?.id
        }]);

      if (error) throw error;

      onSuccess();
      onClose();
      setFormData({
        contact_id: '',
        provider_id: '',
        service_id: '',
        appointment_date: '',
        status: 'scheduled',
        price_paid: 0,
        notes: ''
      });
      setSearchContact('');
    } catch (error) {
      console.error('Error creating appointment:', error);
      alert('Errore durante la creazione dell\'appuntamento');
    } finally {
      setLoading(false);
    }
  };

  const filteredContacts = contacts.filter(contact => {
    return contact.name.toLowerCase().includes(searchContact.toLowerCase());
  });

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Calendar className="h-6 w-6 text-blue-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900">Nuovo Appuntamento</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="h-6 w-6 text-gray-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
              <User className="h-4 w-4" />
              Cliente *
            </label>
            <input
              type="text"
              value={searchContact}
              onChange={(e) => setSearchContact(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent mb-2"
              placeholder="Cerca cliente..."
            />
            <select
              required
              value={formData.contact_id}
              onChange={(e) => setFormData({ ...formData, contact_id: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              size={5}
            >
              <option value="">Seleziona cliente</option>
              {filteredContacts.map((contact) => (
                <option key={contact.id} value={contact.id}>
                  {contact.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
              <Building2 className="h-4 w-4" />
              Fornitore *
            </label>
            <select
              required
              value={formData.provider_id}
              onChange={(e) => setFormData({ ...formData, provider_id: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Seleziona fornitore</option>
              {providers.map((provider) => (
                <option key={provider.id} value={provider.id}>
                  {provider.name} {provider.business_name && `- ${provider.business_name}`}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
              <Sparkles className="h-4 w-4" />
              Servizio (opzionale)
            </label>
            <select
              value={formData.service_id}
              onChange={(e) => setFormData({ ...formData, service_id: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              disabled={!formData.provider_id}
            >
              <option value="">Nessun servizio specifico</option>
              {filteredServices.map((service) => (
                <option key={service.id} value={service.id}>
                  {service.name} - €{service.price}
                </option>
              ))}
            </select>
            {!formData.provider_id && (
              <p className="text-sm text-gray-500 mt-1">Seleziona prima un fornitore</p>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Data e Ora *
              </label>
              <input
                type="datetime-local"
                required
                value={formData.appointment_date}
                onChange={(e) => setFormData({ ...formData, appointment_date: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Stato
              </label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="scheduled">Programmato</option>
                <option value="completed">Completato</option>
                <option value="cancelled">Cancellato</option>
                <option value="no_show">No Show</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
              <DollarSign className="h-4 w-4" />
              Prezzo Pagato (€)
            </label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={formData.price_paid}
              onChange={(e) => setFormData({ ...formData, price_paid: parseFloat(e.target.value) || 0 })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="0.00"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Note
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Note aggiuntive..."
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
            >
              Annulla
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Creazione...' : 'Crea Appuntamento'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NewImageConsultantAppointmentModal;
